import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../lib/supabaseClient';

// Wizz (nudge façon MSN Messenger) : un bouton dans le composer envoie un
// broadcast realtime éphémère sur le canal `wizz:${channelId}`. Les destinataires
// qui ont CE canal ouvert (donc souscrit) jouent le son et voient la fenêtre
// trembler. Aucun écrit en base : pattern calqué sur useTypingPresence.

const WIZZ_SOUND_URL = '/msn-wizz.mp3';
const SHAKE_DURATION_MS = 600; // doit correspondre à .cime-wizz-shake dans index.css
const COOLDOWN_MS = 10_000; // anti-spam : 1 wizz / 10 s / canal

export interface WizzControls {
  /** true pendant ~600ms après réception d'un wizz — applique .cime-wizz-shake */
  isShaking: boolean;
  /** false pendant le cooldown de 10s ou si le canal n'est pas prêt */
  canWizz: boolean;
  /** envoie un wizz aux autres membres du canal (no-op si cooldown/non prêt) */
  sendWizz: () => void;
}

export function useWizz(channelId: string | null): WizzControls {
  const collaborator = useAuthStore((s) => s.collaborator);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isShaking, setIsShaking] = useState(false);
  const [canWizz, setCanWizz] = useState(false);

  // Précharge le son une fois (réutilisé à chaque wizz).
  useEffect(() => {
    if (typeof Audio === 'undefined') return;
    const audio = new Audio(WIZZ_SOUND_URL);
    audio.preload = 'auto';
    audioRef.current = audio;
    return () => {
      audioRef.current = null;
    };
  }, []);

  const triggerLocalWizz = useCallback(() => {
    // Son (best-effort : peut être bloqué si l'utilisateur n'a jamais interagi).
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      void audio.play().catch(() => {
        /* autoplay bloqué : on garde le tremblement visuel */
      });
    }
    // Tremblement : on force un retrait/ré-ajout pour rejouer l'animation
    // même si un wizz arrive pendant qu'un autre est en cours.
    setIsShaking(false);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    requestAnimationFrame(() => {
      setIsShaking(true);
      shakeTimerRef.current = setTimeout(() => setIsShaking(false), SHAKE_DURATION_MS);
    });
  }, []);

  useEffect(() => {
    if (!channelId) {
      setCanWizz(false);
      return;
    }

    const ch = supabase
      .channel(`wizz:${channelId}`)
      .on(
        'broadcast',
        { event: 'wizz' },
        ({ payload }: { payload: { id: string } }) => {
          // Ignore son propre wizz (l'expéditeur ne se secoue pas lui-même).
          if (payload.id === collaborator?.id) return;
          triggerLocalWizz();
        },
      )
      .subscribe((status) => {
        setCanWizz(status === 'SUBSCRIBED');
      });

    channelRef.current = ch;

    return () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      supabase.removeChannel(ch);
      channelRef.current = null;
      setIsShaking(false);
      setCanWizz(false);
    };
  }, [channelId, collaborator?.id, triggerLocalWizz]);

  const sendWizz = useCallback(() => {
    if (!collaborator || !channelRef.current || !canWizz) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'wizz',
      payload: { id: collaborator.id, name: collaborator.full_name },
    });
    // Feedback local immédiat : l'expéditeur voit aussi sa fenêtre trembler.
    triggerLocalWizz();
    // Cooldown anti-spam.
    setCanWizz(false);
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => setCanWizz(true), COOLDOWN_MS);
  }, [collaborator, canWizz, triggerLocalWizz]);

  return { isShaking, canWizz, sendWizz };
}
